import { Router } from "@angular/router";
import { Injectable } from "@angular/core";
import { Buffer } from "buffer";
import { HttpClient, HttpHeaders, HttpEventType } from "@angular/common/http";
import { map, catchError, tap } from "rxjs/operators";
import { environment } from "../../environments/environment";
import { SweetAlertComponent } from "../shared/SweetMesssage";
import { EMPTY, BehaviorSubject, Observable, Subject } from "rxjs";
import { Base } from "../shared/model/core/base.model";
import { UrlConstantes } from "../utilerias/constantes/urlconstantes";
import { deFondo } from "./helper/peticion-de-fondo";
import { Menu } from "../shared/model/security/menu.model";
import { Usuario } from "../shared/model/security/usuario.model";
import { UserSession } from "../shared/model/core/UserSession.model";
import { WebSocketAPI } from "../utilerias/WebSocketAPI";
import { ToptSecrets } from "../shared/model/security/toptsecrets.model";
import swal from "sweetalert2";

const API_URL = environment.apiUrl;

@Injectable({
  providedIn: "root",
})
export class AuthService {
  intentos = 0;
  bandera: Boolean;
  user: Usuario;
  IDUser: BigInteger;

  private fotoPerfilUser = new BehaviorSubject<string>("");
  /** Se emite cada vez que el menú del usuario se vuelve a leer del servidor; el sidebar se repinta. */
  readonly menuCambiado = new Subject<Menu[]>();
  public customFotoPerfilUser = this.fotoPerfilUser.asObservable();

  constructor(
    private router: Router,
    private http: HttpClient,
    private mensaje: SweetAlertComponent,
    private socket: WebSocketAPI
  ) {}

  public changeFotoPerfil(foto: string): void {
    this.fotoPerfilUser.next(foto);
  }

  loginUser(usuario: string, password: string) {
    const headers = new HttpHeaders({
      "Content-Type": "application/json",
      Auth: window.btoa("ADMIN"),
      "X-Requested-With": "XMLHttpRequest",
      authorization: this.createBasicAuthToken(usuario, password),
    });

    this.user = new Usuario();
    this.user.user_nom = usuario;
    this.user.password = btoa(password);
    this.user.intentos = this.intentos;
    this.http
      .post(UrlConstantes.loginAdmin, this.user, {
        headers: headers,
        responseType: "text",
        observe: "response",
      })
      .subscribe({
        next: (resp) => {
          const b: Usuario = JSON.parse(resp.body);
          if (b.pCodRetorno === "00000") {
            localStorage.setItem("CosTk", resp.headers.get("x-auth-token"));
            this.socket._connect();
            this.getMenus();
          } else {
            this.intentos = b.intentos;
            this.alertaLogin(
              "Acceso denegado",
              b.pMensaje || "Usuario o contraseña incorrectos."
            );
          }
        },
        error: (err) => {
          // El servidor rechaza con 401 un usuario inexistente, una
          // contrasena incorrecta, una cuenta de baja o bloqueada, y trae
          // el motivo en la cabecera MESSAGE. Solo lo demas es conexion.
          if (err?.status === 401) {
            this.alertaLogin(
              "Acceso denegado",
              err.headers?.get("MESSAGE") || "Usuario o contraseña incorrectos."
            );
            return;
          }
          this.alertaLogin(
            "Error de conexión",
            "No fue posible iniciar sesión. Verifica tu conexión e intenta de nuevo."
          );
        },
      });
  }

  private alertaLogin(titulo: string, mensaje: string) {
    swal.fire({
      icon: "error",
      title: titulo,
      text: mensaje,
      confirmButtonText: "Entendido",
      buttonsStyling: false,
      customClass: { popup: "swal-dark", confirmButton: "swal-dark__btn" },
    });
  }

  createBasicAuthToken(username: string, password: string) {
    return "Basic " + window.btoa(username + ":" + btoa(password));
  }

  /**
   * Vuelve a pedir los menús del usuario y los deja en localStorage, SIN
   * navegar a ningún lado. Sirve para cuando algo crea un menú en caliente
   * (Gestión de Riesgos da de alta uno por matriz) y hay que verlo sin
   * cerrar sesión; getMenus() no sirve ahí porque redirige al dashboard.
   *
   * Va marcada como petición de FONDO: la lanza el menú lateral solo, en
   * cada carga, y no debe sacar el velo de "Cargando…" por encima de la
   * pantalla ni tirar la sesión de quien esté escribiendo si contesta 401.
   */
  refrescarMenus(): Observable<Menu[]> {
    // Con tap y no con un subscribe interno: el observable de HttpClient es
    // frio, así que suscribirse aquí Y devolverlo disparaba DOS peticiones y
    // el que llama podía recargar la página antes de que se guardara el menú.
    return this.http
      .post<Menu[]>(UrlConstantes.menusUser, {}, { context: deFondo() })
      .pipe(
        tap((menus: Menu[]) => {
          // Solo se pisa lo guardado si de verdad vino algo. Con la sesion
          // HTTP caducada este endpoint contesta 200 con la lista VACIA, y
          // guardarla dejaria al usuario sin menu hasta el proximo login.
          if (menus && menus.length) {
            localStorage.setItem("menuItems", JSON.stringify(menus));
            this.menuCambiado.next(menus);
          }
        }),
      );
  }

  getMenus() {
    this.http
      .post<Menu[]>(UrlConstantes.menusUser, {})
      .subscribe((menus: Menu[]) => {
        localStorage.setItem("menuItems", JSON.stringify(menus));
        this.getSesion().subscribe({
          next: (session) => {
            localStorage.setItem("CosData", JSON.stringify(session));
            // Por TIPO de usuario, no por num_cliente != "1001": con varias
            // empresas en la plataforma, cualquier empleado que no fuera de
            // Solser habria caido en el portal de clientes.
            const isCliente = String(session?.usuario?.tipoUsuario ?? "").toUpperCase() === "CLIENTE";
            this.router.navigate([isCliente ? "/CLIENTE/incidencias" : "dashboard"]);
          },
          error: () => this.router.navigate(["dashboard"]),
        });
      });
  }

  getMenuList(): Menu[] {
    var retrievedObject = localStorage.getItem("menuItems");
    return JSON.parse(retrievedObject);
  }

  getCosData(): UserSession {
    var retrievedObject = localStorage.getItem("CosData");
    return JSON.parse(retrievedObject);
  }

  logout() {
    this.socket._disconnect();
    this.http
      .get(UrlConstantes.cerrarSession)
      .pipe(
        catchError(() => {
          return EMPTY;
        })
      )
      .subscribe((resp) => {});

    localStorage.removeItem("CosData");
    localStorage.removeItem("CosTk");
    localStorage.removeItem("menuItems");
    this.router.navigate(["security/login"]);
  }
  getToken() {
    return localStorage.getItem("CosTk");
  }
  soloToken(): Boolean {
    return this.bandera;
  }
  setSoloToken(band: Boolean) {
    this.bandera = band;
  }

  isAuthenticated() {
    return localStorage.getItem("CosTk") != null;
  }

  validateSesion(): Observable<any> {
    return this.http.post(API_URL + "controller/renovarSession.do", {});
  }

  uploadFile(file: File) {
    let formData: FormData = new FormData();
    formData.append("file", file, file.name);

    this.http
      .post(API_URL + "controller/cargaArchivo.do", formData, {})
      .subscribe();
  }
  getSesion() {
    return this.http.post<UserSession>(UrlConstantes.getSession, {});
  }

  actualizaPasswordUsuario(usuario: Usuario) {
    usuario.password = btoa(usuario.password);
    usuario.newPassword = btoa(usuario.newPassword);
    return this.http.post<Base>(
      UrlConstantes.actualizaPassUsuario,
      usuario,
      {}
    );
  }

  actualizaGitTokenUsuario(usuario: Usuario) {
    if (usuario.token_personal) {
      usuario.token_personal = Buffer.from(usuario.token_personal).toString(
        "base64"
      );
    }
    if (usuario.token_azure) {
      usuario.token_azure = Buffer.from(usuario.token_azure).toString("base64");
    }
    return this.http.post<Base>(UrlConstantes.guardaActualizaTokenGit, usuario);
  }

  consultpersonfoto(id: Number) {
    return this.http.post(UrlConstantes.fotoPersona, id, {
      responseType: "arraybuffer",
    });
  }

  uploadPersonFoto(formData) {
    return this.http.post<Base>(UrlConstantes.uploadFotoPersona, formData);
  }
  public addFile(elemento: File) {
    const param = new FormData();
    param.append("file", elemento, elemento.name.replace(" ", ""));
    return this.http
      .post(UrlConstantes.uploadFotoPersona, param, {
        reportProgress: true,
        observe: "events",
      })
      .pipe(
        map((event) => {
          switch (event.type) {
            case HttpEventType.UploadProgress:
              const progress = Math.round((100 * event.loaded) / event.total);
              return { status: "progress", message: progress };

            case HttpEventType.Response:
              return event.body;
            default:
              return `Unhandled event: ${event.type}`;
          }
        })
      );
  }

  generarCodigoQRTOPT(): Observable<ArrayBuffer> {
    return this.http.get(UrlConstantes.GENERAQRTOPT, {
      responseType: "arraybuffer",
    });
  }
  validarTOPT(codigo: string): Observable<any> {
    const url = UrlConstantes.VALIDATOPT.replace("{codigo}", codigo);
    return this.http.get(url, {});
  }

  existeTOPT(): Observable<boolean> {
    return this.http.get<boolean>(UrlConstantes.EXISTTOPT);
  }

  consultaSecretos(): Observable<ToptSecrets> {
    return this.http.get<ToptSecrets>(UrlConstantes.CONSULTASECRETOS);
  }

  altaSecreto(secretos: ToptSecrets): Observable<boolean> {
    return this.http.post<boolean>(UrlConstantes.ALTASECRETOS, secretos);
  }
  obtenerTokensUsuario(): Observable<ToptSecrets> {
    return this.consultaSecretos();
  }
}
